import React from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";

/**
 * ConfirmModal — thin composition over the DS Modal primitive for
 * destructive confirmations.
 *
 * Purpose: give admin destructive actions (delete-x) a uniform contract
 * that inherits Phase 6.2's focus-trap + return-focus behaviour, without
 * every consumer re-composing Modal + a Cancel/Confirm button pair.
 *
 * Consumer contract (same testids across all 8 admin ConfirmDialog
 * codemod sites so nothing regresses):
 *
 *   <ConfirmModal
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Delete X?"
 *     description="X data will be removed."
 *     confirmLabel="Delete"
 *     cancelLabel="Cancel"
 *     confirmVariant="danger"     // "danger" (default) | "primary"
 *     onConfirm={handleDelete}
 *     loading={busy}
 *     data-testid="delete-x-confirm"
 *   >
 *     <p>Additional context goes here.</p>
 *   </ConfirmModal>
 */

export function ConfirmModal({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "danger",
  onConfirm,
  loading = false,
  disabled = false,
  children,
  triggerRef,
  "data-testid": testId,
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      triggerRef={triggerRef}
      data-testid={testId}
      footer={
        <>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid={testId ? `${testId}-cancel` : "confirm-modal-cancel"}
          >
            {cancelLabel}
          </Button>
          <Button
            size="sm"
            variant={confirmVariant}
            onClick={onConfirm}
            loading={loading}
            disabled={disabled || loading}
            data-testid={testId ? `${testId}-confirm` : "confirm-modal-confirm"}
          >
            {loading ? `${confirmLabel}…` : confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}

export default ConfirmModal;
